import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Download, Info, TrendingDown, TrendingUp, Zap } from 'lucide-react';

const CarbonAccounting = () => {
    // eslint-disable-next-line no-unused-vars
  const [emissions, setEmissions] = useState([
    { scope: "Scope 1", amount: 1000, unit: "tCO2e", change: -5, sources: [
      { name: "Direct Fuel Combustion", value: 600 },
      { name: "Company Vehicles", value: 400 }
    ]},
    { scope: "Scope 2", amount: 5000, unit: "tCO2e", change: -2, sources: [
      { name: "Purchased Electricity", value: 4000 },
      { name: "Purchased Heat", value: 1000 }
    ]},
    { scope: "Scope 3", amount: 15000, unit: "tCO2e", change: 3, sources: [
      { name: "Purchased Goods and Services", value: 8000 },
      { name: "Business Travel", value: 2000 },
      { name: "Employee Commuting", value: 3000 },
      { name: "Waste Disposal", value: 2000 }
    ]},
  ]);

  const totalEmissions = emissions.reduce((sum, emission) => sum + emission.amount, 0);
  const chartData = emissions.map(emission => ({
    scope: emission.scope,
    amount: emission.amount
  }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Carbon Accounting</h1>
        <Button><Download className="mr-2 h-4 w-4" /> Export Report</Button>
      </div>
      
      <Card className="mb-6">
        <CardContent className="pt-6">
          <h2 className="text-2xl font-bold mb-2">Total Emissions: {totalEmissions} tCO2e</h2>
          <p className="mb-4 text-gray-600">Your organization's carbon footprint across all scopes</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="scope" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="amount" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {emissions.map((emission, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                {emission.scope}
                <Info className="h-4 w-4 text-gray-400" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold mb-2">{emission.amount} {emission.unit}</p>
              <div className="flex items-center mb-4">
                {emission.change > 0 ? (
                  <TrendingUp className="h-4 w-4 text-red-500 mr-1" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-green-500 mr-1" />
                )}
                <span className={emission.change > 0 ? "text-red-500" : "text-green-500"}>
                  {Math.abs(emission.change)}% from last year
                </span>
              </div>
              <h4 className="font-semibold mb-2">Emission Sources:</h4>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={emission.sources}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={50}
                    fill="#8884d8"
                    label
                  >
                    {emission.sources.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="mr-2 h-5 w-5 text-yellow-500" />
            AI-Powered Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-5 space-y-2">
            <li>Your Scope 1 emissions have decreased by 5%, indicating successful direct emission reduction efforts.</li>
            <li>Scope 3 emissions show a 3% increase. Consider engaging with suppliers to address this rise.</li>
            <li>Purchased electricity in Scope 2 is your largest single source of emissions. Exploring renewable energy options could significantly reduce your carbon footprint.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default CarbonAccounting;